import { createFileRoute } from '@tanstack/react-router'
import { useMenuToggle } from '@/features/super-admin/super-admin-shell-context'
import { useState } from 'react'
import { SuperAdminHeader } from '@/features/super-admin/components/SuperAdminHeader'
import { AlertList } from '@/features/super-admin/components/alerts/AlertList'
import {
  useAlerts,
  useAlertSummary,
  useAcknowledgeAlert,
  useResolveAlert,
  useDismissAlert,
} from '@/features/super-admin/hooks/use-alerts'
import { AlertCircle, AlertTriangle, Check, Filter } from 'lucide-react'
import type { superAdmin } from '@repo/shared'

type AlertStatus = superAdmin.AlertStatus
type AlertSeverity = superAdmin.AlertSeverity

export const Route = createFileRoute('/super-admin/alerts/')({
  component: AlertsPage,
})

function AlertsPage() {
  const { onMenuToggle } = useMenuToggle()
  const [statusFilter, setStatusFilter] = useState<AlertStatus | 'all'>('all')
  const [severityFilter, setSeverityFilter] = useState<AlertSeverity | 'all'>('all')
  const [page, setPage] = useState(1)

  const { data: alertsData, isLoading } = useAlerts({
    page,
    page_size: 20,
    status: statusFilter === 'all' ? undefined : statusFilter,
    severity: severityFilter === 'all' ? undefined : severityFilter,
  })

  const { data: summary } = useAlertSummary()

  const acknowledgeMutation = useAcknowledgeAlert()
  const resolveMutation = useResolveAlert()
  const dismissMutation = useDismissAlert()

  const alerts = alertsData?.data || []
  const pagination = alertsData?.pagination

  const handleAcknowledge = async (id: string) => {
    try {
      await acknowledgeMutation.mutateAsync(id)
    } catch (error) {
      console.error('Failed to acknowledge alert:', error)
    }
  }

  const handleResolve = async (id: string, resolution: string) => {
    try {
      await resolveMutation.mutateAsync({ id, resolution })
    } catch (error) {
      console.error('Failed to resolve alert:', error)
    }
  }

  const handleDismiss = async (id: string) => {
    try {
      await dismissMutation.mutateAsync(id)
    } catch (error) {
      console.error('Failed to dismiss alert:', error)
    }
  }

  return (
    <div className="flex flex-col h-full">
      <SuperAdminHeader
        title="Alertas"
        subtitle={`${summary?.active || 0} alertas ativos`}
        onMenuToggle={onMenuToggle}
      />

      <div className="flex-1 p-6 space-y-6 overflow-auto">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button
            onClick={() => {
              setStatusFilter('active')
              setSeverityFilter('all')
            }}
            className={`p-4 rounded-xl border transition-colors text-left ${
              statusFilter === 'active' && severityFilter === 'all'
                ? 'border-red-300 bg-red-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{summary?.active || 0}</p>
                <p className="text-sm text-gray-500">Ativos</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => {
              setStatusFilter('all')
              setSeverityFilter('critical')
            }}
            className={`p-4 rounded-xl border transition-colors text-left ${
              severityFilter === 'critical'
                ? 'border-red-300 bg-red-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{summary?.critical || 0}</p>
                <p className="text-sm text-gray-500">Críticos</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => {
              setStatusFilter('all')
              setSeverityFilter('warning')
            }}
            className={`p-4 rounded-xl border transition-colors text-left ${
              severityFilter === 'warning'
                ? 'border-amber-300 bg-amber-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{summary?.warning || 0}</p>
                <p className="text-sm text-gray-500">Alertas</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => {
              setStatusFilter('acknowledged')
              setSeverityFilter('all')
            }}
            className={`p-4 rounded-xl border transition-colors text-left ${
              statusFilter === 'acknowledged'
                ? 'border-amber-300 bg-amber-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Check className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{summary?.acknowledged || 0}</p>
                <p className="text-sm text-gray-500">Reconhecidos</p>
              </div>
            </div>
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Filter className="w-4 h-4" />
            Filtros:
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AlertStatus | 'all')}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">Todos os status</option>
            <option value="active">Ativos</option>
            <option value="acknowledged">Reconhecidos</option>
            <option value="resolved">Resolvidos</option>
            <option value="dismissed">Descartados</option>
          </select>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as AlertSeverity | 'all')}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">Todas as severidades</option>
            <option value="critical">Crítico</option>
            <option value="warning">Alerta</option>
            <option value="info">Informativo</option>
          </select>
          {(statusFilter !== 'all' || severityFilter !== 'all') && (
            <button
              onClick={() => {
                setStatusFilter('all')
                setSeverityFilter('all')
              }}
              className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Limpar filtros
            </button>
          )}
        </div>

        {/* Alert List */}
        <AlertList
          alerts={alerts}
          loading={isLoading}
          onAcknowledge={handleAcknowledge}
          onResolve={handleResolve}
          onDismiss={handleDismiss}
        />

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Mostrando {(page - 1) * pagination.page_size + 1} a{' '}
              {Math.min(page * pagination.page_size, pagination.total)} de {pagination.total} alertas
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-600">
                Página {page} de {pagination.total_pages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.total_pages}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
